import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { TapCue } from './TapCue';
import { hasSeenGuide, rememberGuide } from './useAlbumGuide';

type Anchor = {
  left: number;
  top: number;
  width: number;
  height: number;
  vw: number;
  vh: number;
  tapX: number;
  tapTop: number;
  tapBottom: number;
};

export function AlbumSpotlight({
  open,
  target,
  mobileTarget,
  section,
  action = false,
  onSkip,
}: {
  open: boolean;
  target: RefObject<HTMLButtonElement | null>;
  mobileTarget?: RefObject<HTMLButtonElement | null>;
  section: 'celebration' | 'memories';
  action?: boolean;
  onSkip: () => void;
}) {
  const [visible, setVisible] = useState(open);
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const card = useRef<HTMLDivElement>(null);
  const [cardHeight, setCardHeight] = useState(190);
  const [shineReady, setShineReady] = useState(false);
  const getTarget = useCallback(
    () => (mobileTarget?.current?.getClientRects().length ? mobileTarget.current : target.current),
    [mobileTarget, target],
  );
  useEffect(() => {
    if (open) {
      setVisible(true);
      return;
    }
    const timer = window.setTimeout(() => setVisible(false), 200);
    return () => window.clearTimeout(timer);
  }, [open]);
  useLayoutEffect(() => {
    if (!open || !getTarget()) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const positionTarget = (behavior: ScrollBehavior) => {
      if (action) return;
      const button = getTarget();
      if (!button) return;
      if (button !== mobileTarget?.current)
        button.scrollIntoView({
          block: window.innerHeight < 520 ? 'start' : 'center',
          behavior,
        });
      if (button.disabled) card.current?.focus({ preventScroll: true });
      else button.focus({ preventScroll: true });
    };
    positionTarget(reduced ? 'instant' : 'smooth');
    let frame = 0;
    let shineTimer = 0;
    setShineReady(false);
    const settleShine = () => {
      window.clearTimeout(shineTimer);
      shineTimer = window.setTimeout(() => setShineReady(true), 180);
    };
    const measure = () => {
      const button = getTarget();
      const rect = button?.getBoundingClientRect();
      if (!rect) return;
      const face = button?.querySelector('.contribution-face')?.getBoundingClientRect();
      setAnchor({
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
        vw: window.innerWidth,
        vh: window.innerHeight,
        tapX: face ? face.right - 9 : rect.right - 14,
        tapTop: face ? face.top + 9 : rect.top + 8,
        tapBottom: face ? face.bottom - 9 : rect.bottom - 8,
      });
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
      settleShine();
    };
    const onViewportResize = () => {
      positionTarget('instant');
      schedule();
    };
    const resize = new ResizeObserver(schedule);
    if (target.current) resize.observe(target.current);
    if (mobileTarget?.current) resize.observe(mobileTarget.current);
    measure();
    settleShine();
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', onViewportResize);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(shineTimer);
      resize.disconnect();
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', onViewportResize);
    };
  }, [open, target, mobileTarget, getTarget, action, section]);
  useEffect(() => {
    if (!open || !visible || action) return;
    // Let a closing menu restore page interactivity before focusing this guide.
    const frame = requestAnimationFrame(() => {
      const button = getTarget();
      if (button?.disabled) card.current?.focus({ preventScroll: true });
      else button?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [visible, section, action, open, getTarget]);
  useEffect(() => {
    if (!open || action) return;
    let focusFrame = 0;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        getTarget()?.focus({ preventScroll: true });
        onSkip();
      }
      if (event.key === 'Tab') {
        cancelAnimationFrame(focusFrame);
        focusFrame = requestAnimationFrame(() => {
          const focused = document.activeElement;
          if (focused && !getTarget()?.contains(focused) && !card.current?.contains(focused) && !focused.closest('.guide-exit')) onSkip();
        });
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, action, onSkip, getTarget]);
  useLayoutEffect(() => {
    const node = card.current;
    if (!open || !node) return;
    const measure = () => setCardHeight(node.scrollHeight);
    const resize = new ResizeObserver(measure);
    resize.observe(node);
    measure();
    return () => resize.disconnect();
  }, [open, visible, anchor?.vw]);
  if (!visible || !anchor) return null;
  const width = Math.min(336, anchor.vw - 32);
  const center = anchor.left + anchor.width / 2;
  const left = Math.max(16, Math.min(center - width / 2, anchor.vw - width - 16));
  const gap = 18;
  const below = anchor.top + anchor.height + gap;
  const belowSpace = Math.max(0, anchor.vh - below - 16);
  const aboveSpace = Math.max(0, anchor.top - gap - 16);
  const placeBelow = belowSpace >= cardHeight || belowSpace >= aboveSpace;
  const maxHeight = placeBelow ? belowSpace : aboveSpace;
  const height = Math.min(cardHeight, maxHeight);
  const top = Math.max(16, Math.min(placeBelow ? below : anchor.top - height - gap, anchor.vh - height - 16));
  const cutout = { left: anchor.left - 4, top: anchor.top - 4, right: anchor.left + anchor.width + 4, bottom: anchor.top + anchor.height + 4 };
  const radius = action ? 20 : 9;
  return createPortal(
    <div className={`album-guide-overlay ${open ? 'is-open' : 'is-closing'}`}>
      <div
        className="album-guide-backdrop"
        aria-hidden="true"
        style={{
          clipPath: action ? undefined : `path(evenodd, "M0 0 H${anchor.vw} V${anchor.vh} H0 Z
            M${cutout.left + radius} ${cutout.top}
            H${cutout.right - radius} Q${cutout.right} ${cutout.top} ${cutout.right} ${cutout.top + radius}
            V${cutout.bottom - radius} Q${cutout.right} ${cutout.bottom} ${cutout.right - radius} ${cutout.bottom}
            H${cutout.left + radius} Q${cutout.left} ${cutout.bottom} ${cutout.left} ${cutout.bottom - radius}
            V${cutout.top + radius} Q${cutout.left} ${cutout.top} ${cutout.left + radius} ${cutout.top} Z")`.replace(/\s+/g, ' '),
        }}
      />
      <div
        className={`album-guide-ring${action ? ' contribution-ring' : ''}`}
        aria-hidden="true"
        data-shine-ready={open && shineReady}
        style={{
          left: anchor.left - 4,
          top: anchor.top - 4,
          width: anchor.width + 8,
          height: anchor.height + 8,
          borderRadius: radius,
          display: action ? 'none' : undefined,
        }}
      />
      {open && !action && shineReady && <TapCue key={`${section}-${action}`} x={anchor.left + anchor.width - 15} y={anchor.top + anchor.height - 12} />}
      {open && !action && (
        <div
          ref={card}
          className="album-guide-card"
          role="region"
          tabIndex={-1}
          aria-label="Album quick guide"
          style={{ left, top, width, maxHeight }}
        >
          <div className="guide-copy">
            <h2>
              {action
                ? section === 'celebration'
                  ? 'Catch a moment.'
                  : 'Share a memory.'
                : section === 'celebration'
                  ? 'The celebration starts here.'
                  : 'A lifetime of memories.'}
            </h2>
            <p id="album-guide-description">
              {action
                ? section === 'celebration'
                  ? 'Tap the camera to take a party photo. You can check it before sharing.'
                  : 'Tap Add a memory to choose an older photo. Review it before sharing.'
                : section === 'celebration'
                  ? 'Tap the highlighted tab to see the party photos and add your own.'
                  : 'Tap Memories with Lloyd for older photos and special moments from the years you’ve shared.'}
            </p>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}

export function GuideExit({ onSkip }: { onSkip: () => void }) {
  return createPortal(
    <button className="guide-exit" onClick={onSkip}>Skip guide <span aria-hidden="true">×</span></button>,
    document.body,
  );
}

export function SubmissionGuide() {
  const [dismissed, setDismissed] = useState(() => hasSeenGuide('submission'));
  if (dismissed) return null;
  return (
    <aside className="submission-guide" aria-label="Before you share">
      <strong>Take a look before you share.</strong>
      <p>Enter your name. A birthday message is optional. Tap Publish photo when you’re ready to share.</p>
      <button
        className="guide-skip"
        onClick={() => {
          rememberGuide('submission');
          setDismissed(true);
        }}
      >
        Got it
      </button>
    </aside>
  );
}
